import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Seo } from '@/components/seo/Seo';
import { Header } from '@/components/Header';
import { z } from 'zod';

const passwordSchema = z
  .string()
  .min(6, 'Password must be at least 6 characters long')
  .max(128, 'Password must be less than 128 characters');

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const { toast } = useToast();
  const navigate = useNavigate();

  const validatePassword = (val: string) => {
    try {
      passwordSchema.parse(val);
      setPasswordError('');
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        setPasswordError(error.errors[0].message);
      }
      return false;
    }
  };

  const handleReset = async () => {
    const isValid = validatePassword(password);
    if (!isValid) return;

    if (password !== confirmPassword) {
      setConfirmError('Passwords do not match');
      return;
    }
    setConfirmError('');

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('complete-password-reset', {
        body: { token, password },
      });

      console.log('complete-password-reset response:', { data, error });

      if (error) {
        // supabase-js wraps FunctionsFetchError: try to extract the real message
        const msg = (error)?.context?.message || error.message || 'Failed to reset password';
        toast({
          title: 'Error',
          description: msg,
          variant: 'destructive',
        });
      } else if (data && !data.success) {
        toast({
          title: 'Error',
          description: data.error || 'Failed to reset password',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Password updated',
          description: 'Your password has been reset successfully. Please sign in.',
        });
        navigate('/auth');
      }
    } catch (err) {
      console.error('complete-password-reset catch:', err);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred',
        variant: 'destructive',
      });
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <>
        <Seo title="Applyt: Reset Password" description="Reset your Applyt password." />
        <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted">
          <Header showNavigation={false} />
          <div className="flex items-center justify-center p-4 pt-20">
            <Card className="w-full max-w-md border-border/50 bg-card/50 backdrop-blur-sm">
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl text-center">Invalid Link</CardTitle>
                <CardDescription className="text-center">
                  This password reset link is invalid or has expired.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button className="w-full" onClick={() => navigate('/auth')}>
                  Back to Sign In
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Seo title="Applyt: Reset Password" description="Set a new password for your Applyt account." />
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted">
        <Header showNavigation={false} />
        <div className="flex items-center justify-center p-4 pt-20">
          <Card className="w-full max-w-md border-border/50 bg-card/50 backdrop-blur-sm">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl text-center">Set New Password</CardTitle>
              <CardDescription className="text-center">
                Enter your new password below
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  placeholder="Enter new password (min. 6 characters)"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (passwordError) validatePassword(e.target.value);
                  }}
                  disabled={loading}
                  className={passwordError ? 'border-destructive' : ''}
                />
                {passwordError && <p className="text-sm text-destructive">{passwordError}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="Confirm your new password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (confirmError) setConfirmError('');
                  }}
                  disabled={loading}
                  className={confirmError ? 'border-destructive' : ''}
                />
                {confirmError && <p className="text-sm text-destructive">{confirmError}</p>}
              </div>
              <Button onClick={handleReset} disabled={loading} className="w-full">
                {loading ? 'Updating...' : 'Update Password'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
